/* Scoped 2D star lens. No dependencies; mobile 24fps cap, explicit pause and reduced-motion support. */
(()=>{
  const mounts=new WeakMap();
  function mount(root){
    if(mounts.has(root))return;
    const hero=root.querySelector('.ns-universe__hero'),canvas=root.querySelector('canvas'),toggle=root.querySelector('[data-universe-motion]');
    const motion=matchMedia('(prefers-reduced-motion: reduce)'),fine=matchMedia('(hover: hover) and (pointer: fine)'),abort=new AbortController(),signal=abort.signal;
    const ctx=canvas?.getContext('2d');
    let width=1,height=1,stars=[],frame=0,visible=true,paused=false,point=null,current={x:0,y:0},last=0,elapsed=0,previous=0,lensPower=0;
    const colors=['#e2c97a','#fff7ef','#a8c9dc','#f1e4be'];
    const mobile=()=>width<750||!fine.matches;
    const enabled=()=>!!ctx&&root.dataset.motion==='true'&&!motion.matches&&!paused;
    const active=()=>enabled()&&visible&&!document.hidden;
    const moving=()=>active()&&(mobile()||point!==null||lensPower>.002);
    const sprites=ctx?colors.map(color=>{
      const sprite=document.createElement('canvas');sprite.width=64;sprite.height=64;const c=sprite.getContext('2d');
      if(c){const g=c.createRadialGradient(32,32,0,32,32,32);g.addColorStop(0,color+'c0');g.addColorStop(.12,color+'70');g.addColorStop(.4,color+'14');g.addColorStop(1,color+'00');c.fillStyle=g;c.fillRect(0,0,64,64);}return sprite;
    }):[];
    function lenses(){
      if(!ctx||root.dataset.motion!=='true'||motion.matches)return[];
      if(!mobile())return lensPower>.002?[{x:current.x,y:current.y,r:145,strength:lensPower,spin:1}]:[];
      const t=elapsed;
      return[
        {x:width*(.5+.31*Math.sin(t*.105+.4)),y:height*(.43+.27*Math.sin(t*.076+1.1)),r:104,strength:.9,spin:1},
        {x:width*(.5+.32*Math.sin(t*.087+3.6)),y:height*(.56+.25*Math.cos(t*.063+.2)),r:92,strength:.72,spin:-1}
      ];
    }
    function draw(){
      if(!ctx)return;
      const fields=lenses();ctx.clearRect(0,0,width,height);
      for(const s of stars){
        let offsetX=0,offsetY=0,influence=0,axis=0;
        // Always distort immutable anchors: no accumulated velocity, followers or trails.
        for(const field of fields){
          const dx=s.x-field.x,dy=s.y-field.y;
          // Reject most stars before distance/trigonometry in the denser field.
          if(Math.abs(dx)>=field.r||Math.abs(dy)>=field.r||dx*dx+dy*dy>=field.r*field.r)continue;
          const d=Math.hypot(dx,dy),q=Math.max(0,1-d/field.r),f=q*q*(3-2*q)*field.strength;
          if(f>0){
            // A travelling angular wave stays alive with a stationary cursor.
            // Local compression and an expanded shoulder reveal the lens, without a disc.
            const a=Math.atan2(dy,dx),wave=Math.sin(elapsed*.72-d*.025),b=a+f*(.32+.2*wave)*field.spin,radial=d*(1+.56*f-.88*f*f+.07*f*Math.sin(elapsed*.83-d*.03));
            offsetX+=Math.cos(b)*radial-dx;offsetY+=Math.sin(b)*radial-dy;
            if(f>influence){influence=f;axis=b+Math.PI/2;}
          }
        }
        const length=Math.hypot(offsetX,offsetY),limit=length>40?40/length:1,x=s.x+offsetX*limit,y=s.y+offsetY*limit;
        const r=s.r*(1+influence*.12),light=Math.min(.92,s.opacity+influence*.06);
        // Rare soft radiance; lensing changes shape/position, not halo size or star count.
        if(s.spark){const size=8+s.r*6;ctx.globalAlpha=s.opacity*.2;ctx.drawImage(sprites[s.color],x-size/2,y-size/2,size,size);}
        ctx.globalAlpha=light;ctx.fillStyle=colors[s.color];ctx.beginPath();ctx.ellipse(x,y,r*(1+influence*.55),r,axis,0,Math.PI*2);ctx.fill();
        if(s.spark){const ray=4+s.r*2;ctx.globalAlpha=light*.58;ctx.strokeStyle=colors[s.color];ctx.lineWidth=.55;ctx.beginPath();ctx.moveTo(x-ray,y);ctx.lineTo(x+ray,y);ctx.moveTo(x,y-ray*.7);ctx.lineTo(x,y+ray*.7);ctx.stroke();ctx.globalAlpha=light;ctx.fillStyle='#fffaf1';ctx.beginPath();ctx.ellipse(x,y,.5,.5,0,0,Math.PI*2);ctx.fill();}
      }
      ctx.globalAlpha=1;
    }
    function stop(){if(frame)cancelAnimationFrame(frame);frame=0;last=0;previous=0;}
    function tick(now){
      frame=0;if(!moving()){last=0;previous=0;return;}
      const interval=1000/(mobile()?24:30),delta=previous?Math.min(100,now-previous):0;previous=now;elapsed+=delta/1000;
      if(!last||now-last>=interval){
        if(!mobile()){
          lensPower+=((point?1:0)-lensPower)*.16;
          if(point){current.x+=(point.x-current.x)*.2;current.y+=(point.y-current.y)*.2;}
          if(lensPower<.002)lensPower=0;
        }
        draw();last=now-((now-last)%interval);
      }
      frame=requestAnimationFrame(tick);
    }
    function schedule(){if(!frame&&moving())frame=requestAnimationFrame(tick);}
    function sync(){
      root.dataset.paused=String(paused||motion.matches||root.dataset.motion!=='true');
      root.dataset.offscreen=String(!visible||document.hidden);
      if(toggle){toggle.hidden=!ctx||root.dataset.motion!=='true'||motion.matches;toggle.textContent=paused?toggle.dataset.resumeLabel:toggle.dataset.pauseLabel;}
      stop();if(visible&&!document.hidden)draw();schedule();
    }
    function resize(){
      if(!ctx)return;
      const box=hero.getBoundingClientRect();width=Math.max(1,box.width);height=Math.max(1,box.height);
      const ratio=Math.min(devicePixelRatio||1,mobile()?1.25:1.5);canvas.width=Math.round(width*ratio);canvas.height=Math.round(height*ratio);ctx.setTransform(ratio,0,0,ratio,0,0);
      let seed=71;const rand=()=>{seed=(seed*16807)%2147483647;return(seed-1)/2147483646;};
      const fieldCount=mobile()?480:1300,bandCount=mobile()?620:1500,galaxyCount=mobile()?240:520,count=fieldCount+bandCount+galaxyCount;
      stars=Array.from({length:count},(_,i)=>{
        let x=rand()*width,y=rand()*height;
        const galaxy=i<galaxyCount,band=i>=galaxyCount&&i<galaxyCount+bandCount,dust=galaxy||band;
        if(galaxy){
          // Flattened spiral arms, with dense small-star cores, not glowing discs.
          const side=i%2,rad=Math.pow(rand(),1.35),theta=rad*10+(i%4<2?0:Math.PI)+(rand()-.5)*.42,size=mobile()?82:155;
          const gx=Math.cos(theta)*rad*size,gy=Math.sin(theta)*rad*size*.32,turn=side?-.55:.45;
          x=width*(side?.84:.15)+gx*Math.cos(turn)-gy*Math.sin(turn);
          y=height*(side?.71:.34)+gx*Math.sin(turn)+gy*Math.cos(turn);
        }else if(band){
          // An irregular diagonal Milky-Way-like band, split by a quiet dust lane.
          const t=rand(),spread=height*(mobile()?.12:.095),strand=(rand()+rand()+rand()-1.5)*spread;
          const lane=strand<0?-spread*.1:spread*.1;
          x=t*width;
          y=height*(.89-.69*t+.045*Math.sin(t*11))+strand+lane;
        }
        const r=dust?.3+rand()*(galaxy?.45:.35):.38+Math.pow(rand(),3)*1.15;
        return{x,y,r,opacity:dust?.32+rand()*.42:.35+rand()*.55,spark:!dust&&(r>1.2||i%103===0),color:dust?(rand()<.72?1:2):Math.floor(rand()*colors.length)};
      });
      sync();
    }
    hero.addEventListener('pointermove',e=>{
      if(!active()||mobile()||e.pointerType==='touch')return;
      const b=hero.getBoundingClientRect();point={x:e.clientX-b.left,y:e.clientY-b.top};if(!frame)current={...point};schedule();
    },{passive:true,signal});
    hero.addEventListener('pointerleave',()=>{point=null;schedule();},{signal});
    toggle?.addEventListener('click',()=>{paused=!paused;sync();},{signal});
    motion.addEventListener('change',sync,{signal});fine.addEventListener('change',resize,{signal});document.addEventListener('visibilitychange',sync,{signal});
    const resizeObserver=new ResizeObserver(resize);resizeObserver.observe(hero);
    const intersection=new IntersectionObserver(entries=>{visible=entries[0].isIntersecting;sync();});intersection.observe(hero);resize();
    mounts.set(root,()=>{abort.abort();resizeObserver.disconnect();intersection.disconnect();stop();mounts.delete(root);});
  }
  const init=scope=>scope.querySelectorAll('[data-ns-universe]').forEach(mount);
  init(document);document.addEventListener('shopify:section:load',e=>init(e.target));document.addEventListener('shopify:section:unload',e=>e.target.querySelectorAll('[data-ns-universe]').forEach(root=>mounts.get(root)?.()));
})();
